/**
 * CartItem — single cart item row with quantity controls and side info.
 * Extracted from CartDrawer for readability and reusability.
 */
import { motion } from 'framer-motion';
import { Minus, Plus, Trash2, Calendar, Coffee, Users } from 'lucide-react';
import { format } from 'date-fns';

const MENU_TYPE_ICONS = {
  daily_meals: { Icon: Calendar, color: 'text-primary' },
  canteen: { Icon: Coffee, color: 'text-blue-600' },
  office_platters: { Icon: Users, color: 'text-purple-600' }
};

export default function CartItem({ item, onUpdateQty, onRemove }) {
  const { Icon, color } = MENU_TYPE_ICONS[item.menu_type] || MENU_TYPE_ICONS.daily_meals;
  const linePrice = ((item.price + (item.sides_total || 0)) * item.quantity).toFixed(2);
  const unitPrice = (item.price + (item.sides_total || 0)).toFixed(2);

  return (
    <motion.div
      layout
      exit={{ opacity: 0, x: -50 }}
      className="flex items-start gap-3 px-5 py-4 border-b border-gray-100"
      data-testid={`cart-item-${item.item_id}`}
    >
      {/* Image */}
      <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
        <img
          src={item.image_url || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=200'}
          alt={item.name}
          className="w-full h-full object-cover"
        />
      </div>

      {/* Details */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="font-semibold text-gray-900 text-sm leading-tight truncate">{item.name}</p>
          <Icon className={`h-3 w-3 flex-shrink-0 ${color}`} />
        </div>
        {item.selected_sides?.length > 0 && (
          <p className="text-xs text-gray-500 mt-0.5">
            + {item.selected_sides.map(s => s.name).join(', ')}
          </p>
        )}
        <div className="flex items-baseline gap-1.5 mt-1">
          <p className="text-primary font-bold text-sm">R{linePrice}</p>
          {item.quantity > 1 && (
            <span className="text-xs text-gray-400">(R{unitPrice} each)</span>
          )}
        </div>
      </div>

      {/* Qty Controls */}
      <div className="flex items-center gap-2 flex-shrink-0 mt-1">
        <button
          onClick={() => onUpdateQty(item, item.quantity - 1)}
          className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
          aria-label="Decrease quantity"
        >
          {item.quantity === 1
            ? <Trash2 className="h-3 w-3 text-red-500" />
            : <Minus className="h-3 w-3 text-gray-600" />
          }
        </button>
        <span className="w-4 text-center font-bold text-sm">{item.quantity}</span>
        <button
          onClick={() => onUpdateQty(item, item.quantity + 1)}
          className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center hover:bg-primary/20 transition-colors"
          aria-label="Increase quantity"
        >
          <Plus className="h-3 w-3 text-primary" />
        </button>
      </div>
    </motion.div>
  );
}
